/**
 * assets/ad-attribution.js
 * ----------------------------------------------------------------------------
 * Felt Right — Ad Click ID Capture for HubSpot Embedded Forms
 *
 * Captures gclid / gbraid / wbraid / fbclid from the landing URL and persists
 * them in a first-party cookie. On pages that embed a HubSpot form, the stored
 * values are written onto the page URL as query parameters named for the
 * HubSpot internal property names; HubSpot's embed reads the parent URL and
 * prefills the matching hidden fields itself.
 *
 * WHY THE URL, NOT THE DOM:
 * Felt Right uses HubSpot's NEW forms embed (js-na2.hsforms.net/forms/embed/…),
 * which renders the form inside a CROSS-ORIGIN iframe. `contentDocument` is
 * null and the iframe carries no class, so DOM injection from the parent page
 * is impossible — verified live on /pages/quietcrew. Query-string prefill is
 * the only channel that crosses that boundary, and it is verified working.
 *
 * INSTALL (two parts, both required):
 *   1. layout/theme.liquid, immediately before {{ content_for_header }}:
 *        <script src="{{ 'ad-attribution.js' | asset_url }}"></script>
 *      Loaded WITHOUT defer/async on purpose — see ordering note below.
 *   2. sections/ud-embeded-forms.liquid, above the form blocks:
 *        <script>window.fr_adApplyToUrl && window.fr_adApplyToUrl();</script>
 *
 * ORDERING:
 * HubSpot's embed script tags carry `defer`, so they execute after HTML
 * parsing. This file is synchronous, and the call in ud-embeded-forms.liquid is
 * an inline synchronous script inside the body. Both therefore run BEFORE the
 * HubSpot embed initialises, which is what guarantees the parameters are on the
 * URL in time. No retries, observers or timers are needed.
 *
 * SCOPE:
 * Capture runs on every page (visitors browse before converting). The URL
 * rewrite runs ONLY where a HubSpot form is rendered, so click IDs are not
 * exposed in the address bar sitewide.
 *
 * CONSENT:
 * Runs unconditionally, outside PieEye consent gating. Approved by Felt Right.
 *
 * PREREQUISITE — HubSpot side:
 * Each property below must exist, have "Show in forms" enabled, and be added to
 * every lead form as a hidden field. Query-string prefill has nothing to fill
 * if the hidden field is not on the form.
 *
 *   google_click_id_auto      Google Click ID Auto
 *   google_braid              Google Braid
 *   google_wbraid             Google WBraid
 *   meta_click_id             Meta Click ID
 *   ad_click_timestamp        Ad Click Timestamp   (date picker property)
 *
 * DEBUG: fr_adAttribution() in the console returns the stored object.
 */
(function () {
  'use strict';

  // Guard against a double install (e.g. theme asset AND GTM).
  if (window.fr_adAttribution) return;

  var COOKIE_NAME = 'fr_ad_attr';
  var COOKIE_DAYS = 90;

  // HubSpot property internal name  ->  URL parameter set by the ad platform.
  // Left side must match the HubSpot internal name exactly. Right side is fixed
  // by Google/Meta — do not change it.
  var FIELD_MAP = {
    google_click_id_auto: 'gclid',
    google_braid: 'gbraid',
    google_wbraid: 'wbraid',
    meta_click_id: 'fbclid'
  };

  var TIMESTAMP_FIELD = 'ad_click_timestamp';

  // ---------- cookie ----------

  function readCookie(name) {
    var match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    if (!match) return null;
    try {
      return JSON.parse(decodeURIComponent(match[1]));
    } catch (e) {
      return null;
    }
  }

  function cookieString(name, value, days, domain) {
    return name + '=' + encodeURIComponent(JSON.stringify(value)) +
      '; expires=' + new Date(Date.now() + days * 864e5).toUTCString() +
      '; path=/' +
      (domain ? '; domain=' + domain : '') +
      '; SameSite=Lax' +
      (location.protocol === 'https:' ? '; Secure' : '');
  }

  function stored(name, value) {
    var got = readCookie(name);
    return !!got && JSON.stringify(got) === JSON.stringify(value);
  }

  function writeCookie(name, value, days) {
    var parts = location.hostname.split('.');

    // Prefer the registrable domain so the cookie survives www / non-www.
    if (parts.length > 1) {
      document.cookie = cookieString(name, value, days, '.' + parts.slice(-2).join('.'));
      if (stored(name, value)) return;
    }

    // The domain write was refused — happens on public suffixes such as
    // *.myshopify.com. Fall back to a host-only cookie so theme previews work.
    document.cookie = cookieString(name, value, days, null);
  }

  // ---------- capture ----------

  function paramsFromUrl() {
    var search = new URLSearchParams(location.search);
    var found = {};
    for (var prop in FIELD_MAP) {
      var val = search.get(FIELD_MAP[prop]);
      if (val) found[prop] = val;
    }
    return found;
  }

  function todayIsoDate() {
    var d = new Date();
    return d.getUTCFullYear() + '-' +
      String(d.getUTCMonth() + 1).padStart(2, '0') + '-' +
      String(d.getUTCDate()).padStart(2, '0');
  }

  function captureAndStore() {
    var fresh = paramsFromUrl();
    var existing = readCookie(COOKIE_NAME) || {};

    if (Object.keys(fresh).length) {
      // A new ad click replaces the old one: last-touch matches how Google and
      // Meta attribute, which keeps HubSpot consistent with Ads reporting.
      fresh[TIMESTAMP_FIELD] = todayIsoDate();
      writeCookie(COOKIE_NAME, fresh, COOKIE_DAYS);
      return fresh;
    }

    // No new click IDs — refresh expiry so an engaged visitor in a long sales
    // cycle does not age out mid-consideration.
    if (Object.keys(existing).length) {
      writeCookie(COOKIE_NAME, existing, COOKIE_DAYS);
    }
    return existing;
  }

  captureAndStore();

  // ---------- hand off to HubSpot via the URL ----------

  // Called from ud-embeded-forms.liquid, before HubSpot's deferred embed runs.
  function applyToUrl() {
    var data = readCookie(COOKIE_NAME);
    if (!data) return false;

    var url = new URL(location.href);
    var changed = false;

    Object.keys(data).forEach(function (prop) {
      if (data[prop] && url.searchParams.get(prop) !== String(data[prop])) {
        url.searchParams.set(prop, data[prop]);
        changed = true;
      }
    });

    if (changed) history.replaceState(history.state, '', url.toString());
    return changed;
  }

  window.fr_adApplyToUrl = applyToUrl;

  window.fr_adAttribution = function () {
    return readCookie(COOKIE_NAME);
  };
})();
